import React, { useEffect, useRef, useState } from "react";
import Masonry from "react-masonry-css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { gsap } from "gsap";
import "./projects.scss";
import {
  commercialProjects,
  residentialProject,
  officeSpaceProjects,
} from "../../data/ProjectsData";
import SectionHeader from "../../components/section_header/SectionHeader";
import { useNavigate } from "react-router";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Helmet } from "react-helmet";
const Projects = () => {
  const [loadedImages, setLoadedImages] = useState({});
  const imgRefs = useRef([]);

  const navigate = useNavigate();

  const textRef = useRef(null);

  const [allCommercialProjects, setAllCommercialProjects] = useState();
  const [allResidentialProjects, setAllResidentialProjects] = useState();
  const [allOfficeSpaceProjects, setAllOfficeSpaceProjects] = useState();

  useEffect(() => {
    setAllCommercialProjects(commercialProjects);
    setAllResidentialProjects(residentialProject);
    setAllOfficeSpaceProjects(officeSpaceProjects);
  }, []);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: "power4.out" } });

    tl.fromTo(
      textRef.current,
      { y: 10, opacity: 0 }, // start position (100px down)
      { y: 0, opacity: 1, duration: 1, ease: "power2.inOut" } // end position
    );

    tl.fromTo(
      textRef.current,
      { clipPath: "inset(50% 0 50% 0)" }, // start: hidden from center
      {
        clipPath: "inset(0% 0 0% 0)", // end: fully visible
        duration: 1.5,
        ease: "power2.inOut",
      }
    );
  }, []);

  const handleImageLoad = (index) => {
    setLoadedImages((prev) => ({ ...prev, [index]: true }));
  };

  useEffect(() => {
    // When all images are loaded, refresh ScrollTrigger
    if (Object.keys(loadedImages).length > 0) {
      setTimeout(() => {
        ScrollTrigger.refresh();
      }, 300);
    }
  }, [loadedImages]);

  // Animate image once loaded
  useEffect(() => {
    const lastLoadedIndex = Object.keys(loadedImages).at(-1);
    const imgEl = imgRefs.current[lastLoadedIndex];
    if (imgEl) {
      gsap.fromTo(
        imgEl,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
      );
    }
  }, [loadedImages]);

  const breakpointColumnsObj = {
    default: 3,
    1000: 2,
    500: 1,
  };

  return (
    <div className="projects_main">
      <Helmet>
        <title>Projects | RenderX Architects</title>
        <meta
          name="description"
          content="View RenderX Architects' residential, commercial, and office space projects in Chennai including interiors, planning, and exterior elevation work."
        />

        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": "RenderX Architects Projects",
      "about": [
        "Residential Projects",
        "Commercial Projects",
        "Office Space Projects"
      ],
      "url": "https://renderxarchitects.com/projects"
    }
    `}
        </script>
      </Helmet>
      <div className="project_heading">
        <div className="mask">
          <h1 ref={textRef}>PROJECTS</h1>
        </div>
      </div>
      <div className="projects_container">
        <div className="projects_residential">
          <div className="projects_residential_header">
            <SectionHeader name={"RESIDENTIAL"} />
          </div>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column"
          >
            {allResidentialProjects?.map((project, index) => (
              <div key={index} className="masonry-item">
                {/* Skeleton Placeholder */}
                <a href={`/projects/${project.id}`}>
                  <div className="project_inner_info">
                    <p>{project?.name}</p>
                    <p>{project?.location}</p>
                  </div>
                  <div className="img-wrapper">
                    {!loadedImages[index] && (
                      <Skeleton
                        height={300}
                        style={{ width: "100%", borderRadius: "10px" }}
                      />
                    )}

                    <img
                      src={project.coverImg}
                      className="project-img"
                      onLoad={() => handleImageLoad(index)}
                      style={{
                        opacity: loadedImages[index] ? 1 : 0,
                        transition: "opacity 0.5s ease",
                      }}
                    />
                  </div>
                </a>
              </div>
            ))}
          </Masonry>
        </div>
        <div className="projects_commercial">
          <div className="projects_commercial_header">
            <SectionHeader name={"COMMERCIAL"} />
          </div>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column"
          >
            {allCommercialProjects?.map((project, index) => (
              <div key={index} className="masonry-item">
                {/* Skeleton Placeholder */}
                <a href={`/projects/${project.id}`}>
                  <div className="project_inner_info">
                    <p>{project?.name}</p>
                    <p>{project?.location}</p>
                  </div>
                  <div className="img-wrapper">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      {!loadedImages[index] && (
                        <Skeleton
                          height={300}
                          style={{
                            width: "100%",
                            borderRadius: "10px",
                          }}
                        />
                      )}

                      {/* Image stays in DOM (opacity = 0) until loaded */}
                      <img
                        ref={(el) => (imgRefs.current[index] = el)}
                        src={project?.coverImg}
                        alt={`img-${index}`}
                        loading="lazy"
                        onLoad={() => handleImageLoad(index)}
                        style={{
                          width: "100%",
                          borderRadius: "10px",
                          objectFit: "cover",
                          objectPosition: "centere",
                          opacity: loadedImages[index] ? 1 : 0,
                          transition: "opacity 0.6s ease",
                          display: "block", // ✅ never hide with display:none
                        }}
                      />
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </Masonry>
        </div>
        <div className="projects_office">
          <div className="projects_office_header">
            <SectionHeader name={"OFFICE SPACE"} />
          </div>
          <Masonry
            breakpointCols={breakpointColumnsObj}
            className="my-masonry-grid"
            columnClassName="my-masonry-grid_column"
          >
            {allOfficeSpaceProjects?.map((project, index) => (
              <div key={index} className="masonry-item">
                {/* Skeleton Placeholder */}
                <a href={`/projects/${project.id}`}>
                  <div className="project_inner_info">
                    <p>{project?.name}</p>
                    <p>{project?.location}</p>
                  </div>
                  <div className="img-wrapper">
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        borderRadius: "10px",
                        overflow: "hidden",
                      }}
                    >
                      {!loadedImages[index] && (
                        <Skeleton
                          height={300}
                          style={{
                            width: "100%",
                            borderRadius: "10px",
                          }}
                        />
                      )}

                      {/* Image stays in DOM (opacity = 0) until loaded */}
                      <img
                        ref={(el) => (imgRefs.current[index] = el)}
                        src={project?.coverImg}
                        alt={`img-${index}`}
                        loading="lazy"
                        onLoad={() => handleImageLoad(index)}
                        style={{
                          width: "100%",
                          borderRadius: "10px",
                          objectFit: "cover",
                          objectPosition: "centere",
                          opacity: loadedImages[index] ? 1 : 0,
                          transition: "opacity 0.6s ease",
                          display: "block", // ✅ never hide with display:none
                        }}
                      />
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </Masonry>
        </div>
      </div>
    </div>
  );
};

export default Projects;
